import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import AuthService from "@/services/AuthService";

interface Props {
  /** Pre-fill the requested features field (e.g. "NetSuite Integration") */
  prefilledFeature?: string;
  /** Called when the user cancels or after successful submission */
  onClose?: () => void;
}

export default function UpgradeRequestPanel({ prefilledFeature, onClose }: Props) {
  const api = useApi();
  const profile = AuthService.profile;

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");

  const [contactEmail, setContactEmail] = useState(profile?.userName ?? "");
  const [contactName, setContactName] = useState(fullName);
  const [requestedFeatures, setRequestedFeatures] = useState(prefilledFeature ?? "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactEmail.trim() || !requestedFeatures.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.messaging.post("upgrade-request", {
        contactEmail: contactEmail.trim(),
        contactName: contactName.trim() || undefined,
        requestedFeatures: requestedFeatures.trim(),
        notes: notes.trim() || undefined,
      });
      if (resp.ok) {
        const result = await resp.json();
        if (result.success) {
          setSubmitted(true);
        } else {
          setError(result.error ?? "Submission failed. Please try again.");
        }
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 max-w-md mx-auto">
        <div className="w-12 h-12 rounded-full bg-success-green-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-success-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-slate-gray-800">Request Submitted</h3>
        <p className="text-sm text-slate-gray-600">
          Your account manager will be in touch within 1 business day to discuss your upgrade options.
        </p>
        {onClose && (
          <button
            onClick={onClose}
            className="mt-2 px-4 py-2 bg-cornflower-blue-500 text-white rounded text-sm hover:bg-cornflower-blue-600 transition"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg">
      <div>
        <h3 className="text-base font-semibold text-slate-gray-800">Contact Account Manager</h3>
        <p className="text-xs text-slate-gray-500 mt-1">
          Interested in unlocking additional features? Let us know and your account manager will reach out to discuss options.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-crimson-red-50 border border-crimson-red-200 text-sm text-crimson-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="upgrade-features" className="block text-xs font-medium text-slate-gray-700 mb-1">
            Feature(s) you're interested in *
          </label>
          <input
            id="upgrade-features"
            type="text"
            value={requestedFeatures}
            onChange={e => setRequestedFeatures(e.target.value)}
            required
            className="w-full border border-slate-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-cornflower-blue-500 focus:ring-1 focus:ring-cornflower-blue-500/30"
            placeholder="e.g. NetSuite Integration, Enterprise SSO"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="upgrade-email" className="block text-xs font-medium text-slate-gray-700 mb-1">
              Contact Email *
            </label>
            <input
              id="upgrade-email"
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              required
              className="w-full border border-slate-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-cornflower-blue-500 focus:ring-1 focus:ring-cornflower-blue-500/30"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label htmlFor="upgrade-name" className="block text-xs font-medium text-slate-gray-700 mb-1">
              Your Name
            </label>
            <input
              id="upgrade-name"
              type="text"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              className="w-full border border-slate-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-cornflower-blue-500 focus:ring-1 focus:ring-cornflower-blue-500/30"
              placeholder="Jane Smith"
            />
          </div>
        </div>

        <div>
          <label htmlFor="upgrade-notes" className="block text-xs font-medium text-slate-gray-700 mb-1">
            Additional notes (optional)
          </label>
          <textarea
            id="upgrade-notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            className="w-full border border-slate-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-cornflower-blue-500 focus:ring-1 focus:ring-cornflower-blue-500/30 resize-none"
            placeholder="Any context about your use case or timeline…"
          />
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={submitting || !contactEmail.trim() || !requestedFeatures.trim()}
            className="px-4 py-2 bg-cornflower-blue-500 text-white rounded text-sm font-medium hover:bg-cornflower-blue-600 transition disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-slate-gray-600 hover:text-slate-gray-800 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
